import React from "react";
import { useTranslation } from "react-i18next";
import { DeleteCriterionModalState } from "@/components/controls/Questionnaires/POCQuestionnaire";
import {
  CheckboxGroup,
  CheckboxGroupItem
} from "@/components/ui/checkbox-group";
import Input from "@/components/ui/input";
import {
  useDeleteQuestionMutation,
  useUpdateDealQuestionnaireMutation,
  useUpdateQuestionMutation
} from "@/pages/deals/hooks";
import {
  DealQuestionnaireQuestionSettings,
  QuestionResponse,
  QuestionValueType,
  Responses
} from "@/pages/deals/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { Delete, Edit, InfoCircle } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import { filter, isNull, isNumber, map, sortBy } from "lodash";
import MandatoryIndication from "@/components/controls/Questions/MandatoryIndication";
import { EditingState } from "@/components/controls/SuccessCriteria/SuccessCriteria";
import { useQueryClient } from "@tanstack/react-query";

interface Props {
  dealId: string;
  questionnaireId: string;
  questions: DealQuestionnaireQuestionSettings[] | null;
  setQuestions: React.Dispatch<
    React.SetStateAction<DealQuestionnaireQuestionSettings[] | null>
  >;
  localResponses: Responses;
  updateResponse: (
    questionId: string,
    response: QuestionResponse | null
  ) => void;
  setDeleteCriterionModalState: React.Dispatch<
    React.SetStateAction<DeleteCriterionModalState>
  >;
  handleValueChange: (value: QuestionValueType, questionId: string) => void;
  editingState: EditingState | null;
  setEditingState: React.Dispatch<React.SetStateAction<EditingState | null>>;
  onAddNewRow: () => void;
  resetResponses: () => void;
}

const SuccessCriteriaEditMode: React.FC<Props> = ({
  dealId,
  questionnaireId,
  questions,
  setQuestions,
  localResponses,
  updateResponse,
  setDeleteCriterionModalState,
  handleValueChange,
  editingState,
  setEditingState,
  onAddNewRow,
  resetResponses
}) => {
  const { t } = useTranslation();

  const deleteQuestion = useDeleteQuestionMutation();
  const updateQuestion = useUpdateQuestionMutation();
  const updateQuestionnaire = useUpdateDealQuestionnaireMutation();
  const queryClient = useQueryClient();

  const invalidateQuestionnaireSettings = () =>
    queryClient.invalidateQueries({
      queryKey: ["settings/questionnaireSettings"]
    });

  const checkboxQuestions = sortBy(
    filter(
      questions,
      (question) =>
        (question.responseType.type === "checkbox" &&
          question.isSuccessCriteria) ??
        false
    ),
    "questionOrder"
  );

  const handleInnerValueChange = (
    value: QuestionValueType,
    questionId: string
  ) => {
    const existingResponse =
      (localResponses[questionId] as QuestionResponse) || {};

    const newLocalResponse = {
      ...existingResponse,
      response: {
        ...(existingResponse.response || {}),
        innerValue: value
      }
    };

    updateResponse(questionId, newLocalResponse);

    updateQuestionnaire.mutate(
      {
        pathParams: { dealId, questionnaireId },
        data: {
          questions: [
            {
              questionId,
              response: {
                ...(existingResponse.response || {}),
                innerValue: value
              }
            }
          ]
        }
      },
      {
        onError: resetResponses
      }
    );
  };

  const handleCancelEmptyQuestion = (questionId: string) => {
    deleteQuestion.mutate(
      {
        pathParams: { questionId },
        queryParams: {}
      },
      {
        onSuccess: () => {
          setQuestions((prevQuestions) =>
            (prevQuestions || []).filter((q) => q.id !== questionId)
          );
          setEditingState(null);
          invalidateQuestionnaireSettings();
        }
      }
    );
  };

  const handleSaveQuestionText = async (
    questionId: string,
    newText: string,
    onSuccessCallback?: () => void
  ) => {
    updateQuestion.mutate(
      {
        pathParams: { questionId },
        data: {
          questionText: newText
        }
      },
      {
        onSuccess: () => {
          setQuestions((prevQuestions) =>
            (prevQuestions || []).map((q) =>
              q.id === questionId ? { ...q, questionText: newText } : q
            )
          );
          setEditingState(null);
          invalidateQuestionnaireSettings();
          onSuccessCallback?.();
        }
      }
    );
  };

  const handleBlurQuestionText = (questionId: string, value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue === "") {
      handleCancelEmptyQuestion(questionId);
    } else {
      handleSaveQuestionText(questionId, trimmedValue);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    questionId: string,
    value: string
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmedValue = value.trim();
      if (trimmedValue !== "") {
        handleSaveQuestionText(questionId, trimmedValue, onAddNewRow);
      }
    }
  };

  const getActions = (question: DealQuestionnaireQuestionSettings) =>
    [
      {
        icon: <Edit />,
        handler: () => {
          setEditingState({
            questionId: question.id,
            value: question.questionText
          });
        }
      },
      {
        icon: <Delete />,
        handler: () =>
          setDeleteCriterionModalState({
            isOpen: true,
            questionId: question.id
          })
      }
    ] as const;

  const getDefaultInnerValue = (
    question: DealQuestionnaireQuestionSettings
  ) => {
    const type = question.responseType.setValueType;
    const innerValue = (localResponses[question.id]?.response as any)
      ?.innerValue;
    return type === "number" && isNumber(innerValue)
      ? Number(innerValue)
      : innerValue?.toString();
  };

  return (
    <>
      <CheckboxGroup fullWidth spacing={16} color="var(--cta)">
        {map(checkboxQuestions, (question) => {
          const hasSetValueType =
            question.responseType.setValueType &&
            !isNull(question.responseType.setValueType);

          return (
            <div
              key={question.id}
              className="flex items-center group justify-between gap-2"
              onMouseDown={(e) => {
                // Keep focus in the row's text input while its checkbox is ticked,
                // so an empty row isn't blurred and deleted. Other controls in the
                // row still blur the input and save the draft.
                const target = e.target as HTMLElement;
                if (
                  editingState?.questionId === question.id &&
                  !target.closest("input") &&
                  target.closest('[role="checkbox"], label')
                ) {
                  e.preventDefault();
                }
              }}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <CheckboxGroupItem
                  id={question.id}
                  containerClassName={
                    hasSetValueType ? undefined : "flex-1 min-w-0"
                  }
                  label={
                    editingState?.questionId === question.id ? (
                      <div
                        className={
                          hasSetValueType ? undefined : "flex-1 min-w-0"
                        }
                      >
                        <Input
                          className="w-full"
                          value={editingState.value}
                          onChange={(e) =>
                            setEditingState({
                              questionId: question.id,
                              value: e.target.value
                            })
                          }
                          onBlur={() =>
                            handleBlurQuestionText(
                              question.id,
                              editingState.value
                            )
                          }
                          onKeyDown={(e) =>
                            handleKeyDown(e, question.id, editingState.value)
                          }
                          autoFocus
                        />
                      </div>
                    ) : (
                      question.questionText
                    )
                  }
                  labelClassName={
                    hasSetValueType ? "text-base" : "text-base flex-1 min-w-0"
                  }
                  checked={
                    Boolean(localResponses[question.id]?.response?.value) ||
                    question.isMandatory
                  }
                  disabled={question.isMandatory}
                  onCheckedChange={(checked: boolean) => {
                    handleValueChange(checked, question.id);
                  }}
                />
                {hasSetValueType && (
                  <div className="flex gap-2 items-center">
                    <MandatoryIndication />
                    <div className="max-w-36">
                      <Input
                        key={`${question.id}-${getDefaultInnerValue(question)}`}
                        className="mr-1 border-border-border2 placeholder:text-tertiary"
                        placeholder={t(
                          "deals.evaluationQuestionnaire.successCriteria.placeholder"
                        )}
                        defaultValue={getDefaultInnerValue(question)}
                        trailingIcon={
                          <div className="text-primary">
                            {question.responseType.unit}
                          </div>
                        }
                        type={
                          question.responseType.setValueType === "number"
                            ? "number"
                            : "text"
                        }
                        onBlur={(e) => {
                          handleInnerValueChange(e.target.value, question.id);
                        }}
                      />
                    </div>
                  </div>
                )}
                {question.tooltipText && !isNull(question.tooltipText) && (
                  <Tooltip>
                    <TooltipTrigger>
                      <IconContainer color="tertiary">
                        <InfoCircle />
                      </IconContainer>
                    </TooltipTrigger>
                    <TooltipContent maxWidth={18}>
                      {question.tooltipText}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
              {!question.isSystem && (
                <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity duration-200">
                  {map(getActions(question), (action, index) => (
                    <div
                      key={index}
                      className="p-[2px] cursor-pointer transition-colors duration-200 rounded-md hover:bg-surface-elevation2"
                      onClick={action.handler}
                    >
                      <IconContainer>{action.icon}</IconContainer>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </CheckboxGroup>
    </>
  );
};

export default SuccessCriteriaEditMode;
